import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import InputMask from "react-input-mask";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";

interface FilterDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onClearFilters: () => void;
}

// Define type for input props
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  ref?: React.Ref<HTMLInputElement>;
}

const FilterDialog: React.FC<FilterDialogProps> = ({
  isOpen,
  onOpenChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClearFilters,
}) => {
  const [startDateEnabled, setStartDateEnabled] = useState(!!startDate);
  const [endDateEnabled, setEndDateEnabled] = useState(!!endDate);
  const [dateError, setDateError] = useState<string | null>(null);

  /**
   * Converts a Brazilian-style date string "dd/mm/yyyy" to "yyyy-mm-dd".
   * Returns empty string if parsing fails or format is incomplete.
   */
  const parseBrazilianDate = (value: string): string => {
    if (!value.trim()) return '';

    const parts = value.trim().split("/");
    if (parts.length !== 3) {
      return '';
    }

    const [day, month, year] = parts;
    if (day.length < 1 || month.length < 1 || year.length !== 4) {
      return '';
    }

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  useEffect(() => {
    validateDates();
  }, [startDate, endDate, startDateEnabled, endDateEnabled]);

  const validateDates = () => {
    if (startDateEnabled && endDateEnabled && startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setDateError("A data inicial não pode ser posterior à data final");
        return false;
      }
    }
    setDateError(null);
    return true;
  };

  const handleStartDateChange = (value: string) => {
    onStartDateChange(parseBrazilianDate(value) || "");
  };

  const handleEndDateChange = (value: string) => {
    onEndDateChange(parseBrazilianDate(value) || "");
  };

  const handleStartCheckboxChange = (checked: boolean) => {
    setStartDateEnabled(checked);
    if (!checked) onStartDateChange("");
  };

  const handleEndCheckboxChange = (checked: boolean) => {
    setEndDateEnabled(checked);
    if (!checked) onEndDateChange("");
  };

  const hasFilters = (startDateEnabled && startDate) || (endDateEnabled && endDate);
  const isValid = !dateError && (startDateEnabled || endDateEnabled) &&
    (!startDateEnabled || startDate) && (!endDateEnabled || endDate);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Filtrar Promoções</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2" title="Habilite para filtrar por data inicial">
                <Checkbox
                  checked={startDateEnabled}
                  onCheckedChange={handleStartCheckboxChange}
                />
                <label className="text-sm font-medium text-gray-700">
                  Data Inicial
                </label>
              </div>
            </div>
            <div className="relative">
              <InputMask
                mask="99/99/9999"
                disabled={!startDateEnabled}
                value={
                  startDateEnabled && startDate
                    ? (startDate.includes("-") ? 
                        `${startDate.split("-")[2]}/${startDate.split("-")[1]}/${startDate.split("-")[0]}` : startDate )
                    : ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleStartDateChange(e.target.value)}
              >
                {(inputProps: InputProps) => (
                  <Input 
                    {...inputProps} 
                    className={`w-full ${startDate && startDateEnabled ? 'border-blue-500' : ''}`} 
                  />
                )}
              </InputMask>
            </div>
          </div>
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2" title="Habilite para filtrar por data final">
                <Checkbox
                  checked={endDateEnabled}
                  onCheckedChange={handleEndCheckboxChange}
                />
                <label className="text-sm font-medium text-gray-700">
                  Data Final
                </label>
              </div>
            </div>
            <div className="relative">
              <InputMask
                mask="99/99/9999"
                disabled={!endDateEnabled}
                value={
                  endDateEnabled && endDate
                    ? (endDate.includes("-") ? 
                        `${endDate.split("-")[2]}/${endDate.split("-")[1]}/${endDate.split("-")[0]}` : endDate )
                    : ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEndDateChange(e.target.value)}
              >
                {(inputProps: InputProps) => (
                  <Input 
                    {...inputProps} 
                    className={`w-full ${endDate && endDateEnabled ? 'border-blue-500' : ''}`} 
                  />
                )}
              </InputMask>
            </div>
          </div>
          {dateError && (
            <div className="text-sm text-red-500">
              {dateError}
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-between">
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={onClearFilters}
              className="mr-auto"
            >
              <X className="h-4 w-4 mr-1" />
              Limpar Filtros
            </Button>
          )}
          <Button 
            onClick={() => onOpenChange(false)}
            disabled={!isValid}
            title={!isValid ? "Selecione pelo menos uma data válida" : ""}
          >
            Aplicar Filtros
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FilterDialog;